import React, { useEffect, useRef, useState } from 'react';
import EditableContent from '../EditableContent.tsx';
import SiteFooter from '../layout/SiteFooter.tsx';

interface ContactSectionProps {
  isEditMode: boolean;
  getContent: (key: string) => string;
  onContentSave: (contentId: string, value: string, contentType: 'text' | 'image') => Promise<void> | void;
  onDirtyStateChange: (contentId: string, isDirty: boolean) => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({
  isEditMode,
  getContent,
  onContentSave,
  onDirtyStateChange,
}) => {
  const [copyStatus, setCopyStatus] = useState('');
  const [showFallback, setShowFallback] = useState(false);
  const fallbackRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (showFallback && fallbackRef.current) {
      fallbackRef.current.focus();
      fallbackRef.current.select();
    }
  }, [showFallback]);

  const handleCopyInquiry = async () => {
    const text = getContent('inquiry-copy-template');
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('已複製詢價清單，可貼至您的郵件中填寫。');
      setShowFallback(false);
    } catch {
      setCopyStatus('請選取並複製以下清單：');
      setShowFallback(true);
    }
  };

  const editable = (
    contentId: string,
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li' | 'dt' | 'dd' | 'small' | 'strong' | 'em',
    className?: string,
    multiline?: boolean
  ) => (
    <EditableContent
      contentId={contentId}
      contentType="text"
      tag={tag}
      className={className}
      multiline={multiline}
      defaultValue={getContent(contentId)}
      isEditing={isEditMode}
      onSave={onContentSave}
      onDirtyStateChange={onDirtyStateChange}
    />
  );

  return (
    <section id="contact" className="contact">
      <div className="section-head reveal">
        {editable('contact-eyebrow', 'p', 'eyebrow')}
        {editable('contact-tagline', 'span')}
      </div>
      <div className="contact-grid">
        <div className="reveal">
          <h2>
            {editable('contact-heading-line1', 'span')}
            <br />
            {editable('contact-heading-line2', 'span')}
            <span>。</span>
          </h2>
          {editable('contact-intro', 'p', undefined, true)}
          {isEditMode ? (
            <>
              {editable('contact-copy-button-text', 'span')}
              {editable('inquiry-copy-template', 'p', undefined, true)}
            </>
          ) : (
            <button className="button light" id="copy-inquiry" type="button" onClick={handleCopyInquiry}>
              {getContent('contact-copy-button-text')} <span>↗</span>
            </button>
          )}
          <p id="copy-status" className="copy-status" role="status">
            {copyStatus}
            {showFallback && (
              <textarea
                ref={fallbackRef}
                value={getContent('inquiry-copy-template')}
                rows={12}
                readOnly
                aria-label="詢價清單"
              />
            )}
          </p>
        </div>
        <div className="contact-details reveal">
          <h3 className="contact-company">
            {getContent('company-title')} {getContent('company-title-en')}
          </h3>
          <div>
            <span>PHONE / 電話</span>
            {editable('contact-phone', 'p')}
          </div>
          <div>
            <span>EMAIL / 電子郵件</span>
            {editable('contact-email', 'p')}
          </div>
          <div>
            <span>LOCATION / 公司地址</span>
            {editable('contact-address', 'p')}
          </div>
          {editable('contact-note', 'small')}
        </div>
      </div>
      <SiteFooter variant="home" getContent={getContent} />
    </section>
  );
};

export default ContactSection;
